import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useSubmissionComplete } from '@/features/application/hooks/use-submission-complete.hook';
import { logger } from '@/features/help/utils/logger';
import { Stack } from '@/features/ui/components/Stack';
import { Text } from '@/features/ui/components/Text';

// Purpose: Show a completion message after successful submission
export const SubmissionCompletePage = () => {
  const { userShortId, mappingShortId } = useParams<{
    userShortId: string;
    mappingShortId: string;
  }>();
  const [searchParams] = useSearchParams();
  const crmId = searchParams.get('crmId') || null;
  const signerIndex = searchParams.get('signerIndex') || null;
  const pdfOnly = searchParams.get('pdfOnly') === '1';

  const completionPath = useMemo(() => {
    const query = searchParams.toString();
    return `/application/${userShortId}/${mappingShortId}/submission-complete${
      query ? `?${query}` : ''
    }`;
  }, [mappingShortId, searchParams, userShortId]);

  useSubmissionComplete({ completionPath });

  logger.info('SubmissionComplete', 'Rendering submission complete page', {
    userShortId,
    mappingShortId,
    crmId,
    signerIndex,
    pdfOnly,
  });

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-xl rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm">
        <Stack spacing={4} className="items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
            <CheckCircle className="h-7 w-7 text-success" />
          </div>
          <Text asChild variant="pageTitle">
            <h1>Success!</h1>
          </Text>
          <Text asChild variant="body">
            <p>Your application has been submitted. You can close this window.</p>
          </Text>
        </Stack>
      </div>
    </div>
  );
};
